/* Copyright (c) 2020-present CloudIO Inc */

import { IoAppDevelopersV, Query } from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { AppUidChooserProp } from 'componentregistry';
import { VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useMemo } from 'react';
import useAppStore from 'store/useAppStore';
import { getIoAppDevelopersVStoreProps } from 'store/utils/utils';
import WaveDots from 'utils/WaveDots';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

export const dsFilter: (appUid?: string) => Query<IoAppDevelopersV> = (
  appUid,
) => ({
  filter: [],
  limit: 5000,
  offset: 0,
  projection: {
    appUid: 1,
    appName: 1,
    appIcon: 1,
  },
});

export function AppUidChooser({
  appUid,
  helpText,
  label,
  onChange,
  required,
  testid,
  value,
  includeCloudIODataSources = true,
  isViewOnlyUser = false,
}: {
  appUid: string;
  helpText?: string;
  label: string;
  onChange: (val: string) => void;
  required?: boolean;
  testid: string;
  value: string;
  includeCloudIODataSources?: boolean;
  isViewOnlyUser?: boolean;
}) {
  const { query, records, isBusy } = useAppStore<IoAppDevelopersV>(
    getIoAppDevelopersVStoreProps(),
  );
  useEffect(() => {
    query({ props: dsFilter(includeCloudIODataSources ? undefined : appUid) });
  }, [appUid, includeCloudIODataSources, query]);
  const classes = useStyles();

  const options = useMemo(
    () =>
      Object.values(records).map((row) => ({
        value: row.appUid,
        label: row.appName,
      })),
    [records],
  );

  return (
    <FormControl
      data-testid={testid}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={options}
        onChange={onChange}
        label={label}
        startIcon={isBusy ? <WaveDots /> : null}
        disabled={isBusy || isViewOnlyUser}
        placeholder={isBusy ? 'Loading ...' : 'Choose an app'}
        variant="standard"
        required={required}
        disableClearable={false}
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

function AppUidChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<AppUidChooserProp, string>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (ds: string) => {
    if (ds !== value) {
      onChange(property, ds);
    }
  };

  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <AppUidChooser
      isViewOnlyUser={isViewOnlyUser}
      appUid={appUid}
      testid={property}
      value={value}
      onChange={handleChange}
      label={def.label}
      required={required}
      helpText={helpText}
    />
  );
}

export default memo(AppUidChooserProperty);
